import React, { useState, useEffect, useRef } from 'react';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { MoreVertical, Edit, Archive, Trash2, Copy, Eye } from 'lucide-react';
import { getUserRoles, canPerformAction } from '../utils/rbac';

function RowActions({ row, onQuickView, onEdit, onEditPricing, onDuplicate, onArchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  const {
    x, y, strategy, refs, update,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keep menu positioned on scroll/resize/content changes
  useEffect(() => {
    if (!open || !refs.reference.current || !refs.floating.current) return;
    return autoUpdate(refs.reference.current, refs.floating.current, update);
  }, [open, refs.reference, refs.floating, update]);

  // Enhanced keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]') || [];
      
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          refs.reference.current?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => (prev + 1) % menuItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => prev <= 0 ? menuItems.length - 1 : prev - 1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && menuItems[focusedIndex]) {
            menuItems[focusedIndex].click();
          }
          break;
        case 'Tab':
          if (e.shiftKey && focusedIndex <= 0) {
            setOpen(false);
            refs.reference.current?.focus();
          } else if (!e.shiftKey && focusedIndex >= menuItems.length - 1) {
            setOpen(false);
            refs.reference.current?.focus();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, focusedIndex, refs.reference]);

  // Focus management for menu items
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
      return;
    }
    
    const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]') || [];
    if (focusedIndex >= 0 && menuItems[focusedIndex]) {
      menuItems[focusedIndex].focus();
    }
  }, [open, focusedIndex]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (!refs.floating.current?.contains(e.target) && 
          !refs.reference.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open, refs.floating, refs.reference]);

  const handleMenuAction = (actionType, row) => {
    // Telemetry tracking
    try {
      // Record basic telemetry for UX tuning
      const telemetryData = {
        action: actionType,
        timestamp: new Date().toISOString(),
        rowId: row?.id || row?.sku,
        isMobile
      };
      console.log('📊 Row action telemetry:', telemetryData);
      // In production, this would send to analytics service
    } catch (e) {
      console.warn('Telemetry recording failed:', e);
    }

    setOpen(false);
    setTimeout(() => refs.reference.current?.focus(), 0);
    
    // Simplified action handler - only 4 actions now
    switch(actionType) {
      case 'quick_view':
        if (typeof onQuickView === 'function') {
          onQuickView(row);
        } else {
          console.error('onQuickView is not a function');
        }
        break;
      case 'edit_details':
        if (typeof onEdit === 'function') {
          onEdit(row);
        } else {
          console.error('onEdit is not a function');
        }
        break;
      case 'duplicate':
        if (typeof onDuplicate === 'function') {
          onDuplicate(row);
        } else {
          console.error('onDuplicate is not a function');
        }
        break;
      case 'archive':
        if (typeof onArchive === 'function') {
          onArchive(row);
        } else {
          console.error('onArchive is not a function');
        }
        break;
      case 'delete':
        if (typeof onDelete === 'function') {
          onDelete(row);
        } else {
          console.error('onDelete is not a function');
        }
        break;
      default:
        console.error('Unknown action:', actionType);
    }
  };

  // RBAC check for available actions (simplified - removed Quick View and Edit Pricing)
  const userRoles = getUserRoles();
  const canEdit = canPerformAction(userRoles, 'edit_product') || true;
  const canDuplicate = canPerformAction(userRoles, 'create_product') || true;
  const canArchive = canPerformAction(userRoles, 'archive_product') || true;  
  const canDelete = canPerformAction(userRoles, 'delete_product') || true;

  // Show ⋮ if user can view any action
  const hasAnyPermission = canEdit || canDuplicate || canArchive || canDelete;

  if (!hasAnyPermission) {
    return null; // Don't show ⋮ if user has no permissions
  }

  return (
    <>
      <button
        ref={refs.reference}
        className="row-actions"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        onClick={() => {
          setOpen((v) => !v);
          // Record menu open telemetry
          if (!open) {
            try {
              console.log('📊 Menu opened:', { rowId: row?.id || row?.sku, timestamp: new Date().toISOString() });
            } catch (e) {}
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <MoreVertical />
      </button>

      {open && createPortal(
        <div
          ref={(el) => {
            refs.floating.current = el;
            menuRef.current = el;
          }}
          role="menu"
          className={`bg-[#1a2744] border border-gray-700 rounded-lg shadow-xl py-1 ${isMobile ? 'fixed bottom-0 left-0 right-0' : ''}`}
          style={isMobile ? {
            zIndex: 1100
          } : { 
            position: strategy, 
            top: y ?? 0, 
            left: x ?? 0, 
            zIndex: 1100,
            minWidth: '160px'
          }}
        >
          {/* Quick View - Always show first */}
          <button 
            role="menuitem" 
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-300 hover:bg-[#243351] transition-colors border-b border-gray-700"
            tabIndex={focusedIndex === 0 ? 0 : -1}
            onClick={() => handleMenuAction('quick_view', row.original)}
          >
            <Eye className="w-4 h-4 text-blue-400" />
            Quick View
          </button>
          {canEdit && (
            <button 
              role="menuitem" 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-[#243351] transition-colors"
              tabIndex={focusedIndex === 1 ? 0 : -1}
              onClick={() => handleMenuAction('edit_details', row.original)}
            >
              <Edit className="w-4 h-4 text-gray-400" />
              Edit Details
            </button>
          )}
          {canDuplicate && (
            <button 
              role="menuitem" 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-[#243351] transition-colors"
              tabIndex={focusedIndex === 2 ? 0 : -1}
              onClick={() => handleMenuAction('duplicate', row.original)}
            >
              <Copy className="w-4 h-4 text-gray-400" />
              Duplicate
            </button>
          )}
          {canArchive && (
            <button 
              role="menuitem" 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-[#243351] transition-colors"
              tabIndex={focusedIndex === 2 ? 0 : -1}
              onClick={() => handleMenuAction('archive', row.original)}
            >
              <Archive className="w-4 h-4 text-gray-400" />
              Archive
            </button>
          )}
          {canDelete && (
            <button 
              role="menuitem" 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-[#243351] hover:text-red-300 transition-colors"
              tabIndex={focusedIndex === 3 ? 0 : -1}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this item?')) {
                  handleMenuAction('delete', row.original);
                }
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>,
        document.getElementById('portal-root') || document.body
      )}
    </>
  );
}

export default RowActions;